import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  EmptyState,
  Icon,
  NoResults,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { PaymentLimitsTable } from 'components/PaymentLimits/PaymentLimitsTable'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { PaymentLimitsFilters } from 'pages/PaymentLimits/components/PaymentLimitsFilters'
import { PaymentLimitSheet } from 'pages/PaymentLimits/components/PaymentLimitSheet'
import { usePaymentLimitsFilters } from 'pages/PaymentLimits/hooks/usePaymentLimitsFilters'
import { LimitLevel, PaymentLimitSheetType } from 'types/paymentLimits'

interface CustomerPaymentLimitsTableProps {
  customerReference: string
}

export const CustomerPaymentLimitsTable = ({
  customerReference,
}: CustomerPaymentLimitsTableProps) => {
  const { t } = useTranslation()
  const permissions = useMappedRoles()
  const canEditPaymentLimits = permissions?.paymentLimits?.canEdit ?? false

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)

  const {
    filterProps,
    limits,
    totalElements,
    hasLimits,
    hasActiveFilters,
    isFetching,
    isError,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePaymentLimitsFilters({ level: LimitLevel.CUSTOMER, customerId: customerReference })

  const showFilters = hasLimits || hasActiveFilters || isFetching
  const showEmptyState = !isFetching && !isError && !hasLimits && !hasActiveFilters
  const showNoResults = !isFetching && !isError && !hasLimits && hasActiveFilters

  if (showEmptyState) {
    return (
      <>
        <EmptyState
          testId="customer-payment-limits-empty-state"
          lottieName="welcome"
          title={t('customers.paymentLimitsTable.emptyStateTitle')}
          description={t('customers.paymentLimitsTable.emptyStateDescription')}
          {...(canEditPaymentLimits && {
            PrimaryButton: (
              <Button
                testId="add-customer-limit-button"
                variant="secondary"
                iconElement={<Icon name="Plus" />}
                onClick={() => setIsAddSheetOpen(true)}
              >
                {t('paymentLimits.addLimit')}
              </Button>
            ),
          })}
        />

        <PaymentLimitSheet
          isOpen={isAddSheetOpen}
          type={PaymentLimitSheetType.ADD}
          customerId={customerReference}
          onClose={() => setIsAddSheetOpen(false)}
        />
      </>
    )
  }

  return (
    <>
      <Box flex justify="between" align="center" paddingB={16} gapX={12}>
        {showFilters && <PaymentLimitsFilters {...filterProps} />}
        {canEditPaymentLimits && (
          <Button
            testId="add-customer-limit-button"
            variant="secondary"
            iconElement={<Icon name="Plus" />}
            onClick={() => setIsAddSheetOpen(true)}
          >
            {t('paymentLimits.addLimit')}
          </Button>
        )}
      </Box>

      {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {hasLimits && (
        <PaymentLimitsTable
          limits={limits}
          tableName="CustomerPaymentLimitsTable"
          customerId={customerReference}
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={totalElements}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}

      {showNoResults && <NoResults dataName="payment limits" />}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        customerId={customerReference}
        onClose={() => setIsAddSheetOpen(false)}
      />
    </>
  )
}
